import React, { useState, useEffect } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { SIREN_BOARD_TITLE_HEIGHT } from "config.js";
import firebase from "../../Firebase";
import "firebase/database";

const DATE_KEY_FORMAT = "YYYYMMDD";

/**
 * 대시보드 최상단 "이번 주 AI 브리핑" 카드
 *
 * 배치(sirencare_batch/AiInsightService.py, 매일 05:00)가 미리 계산해서
 * /dashboard/{facility_id}/aiInsight/{yyyymmdd}에 저장해둔 값을 읽기만 한다.
 * (계산 로직은 전부 배치 쪽에 있음 — 프론트는 규칙 기반 재계산을 하지 않는다.)
 *
 * 오늘자 값이 아직 없으면(예: 자정~오전 5시 사이, 배치 실행 전) 어제자 값으로 대체 표시한다.
 */
const AiBriefingBoard = (props) => {
  const { className, ...rest } = props;
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const facility_id = useSelector((store) => store.user.facility_id);

  const [loading, setLoading] = useState(true);
  const [briefing, setBriefing] = useState(null);
  const [error, setError] = useState(false);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    if (!facility_id) return;

    setLoading(true);
    setError(false);
    setNoData(false);

    const todayKey = moment().format(DATE_KEY_FORMAT);
    const yesterdayKey = moment().subtract(1, "days").format(DATE_KEY_FORMAT);

    const dbRef = firebase.database().ref("/dashboard/" + facility_id).child("aiInsight");

    const handleValue = (snapshot) => {
      const allInsights = snapshot.val() || {};
      const todayData = allInsights[todayKey];
      const data = todayData || allInsights[yesterdayKey] || null;

      if (data) {
        setBriefing(data);
        setNoData(false);
      } else {
        setBriefing(null);
        setNoData(true);
      }
      setLoading(false);
    };

    const handleError = (err) => {
      console.error("AiBriefingBoard firebase read error:", err);
      setError(true);
      setLoading(false);
    };

    dbRef.on("value", handleValue, handleError);

    return () => {
      dbRef.off("value", handleValue);
    };
  }, [facility_id]);

  if (!facility_id) {
    return null;
  }

  //배치(AiInsightService.py)가 저장한 원자료를 언어별 문장으로 조립한다.
  //(구버전 배치가 저장한, 이미 조립된 한국어 문장(summaryLine1/2)만 있는 데이터는 그대로 표시한다.)
  const buildLines = (data) => {
    if (!data) return { line1: "", line2: "" };

    if (data.changeStatus == null && data.summaryLine1){
      return { line1: data.summaryLine1, line2: data.summaryLine2 || "" };
    }

    let changeText;
    if (data.changeStatus === "none"){
      changeText = formatMessage({ id: "ai_briefing_change_none" });
    } else if (data.changeStatus === "no_last_week"){
      changeText = formatMessage({ id: "ai_briefing_change_no_last_week" });
    } else if (data.changeRate >= 0){
      changeText = formatMessage({ id: "ai_briefing_change_increase" }, { rate: data.changeRate });
    } else {
      changeText = formatMessage({ id: "ai_briefing_change_decrease" }, { rate: Math.abs(data.changeRate) });
    }

    const line1 = formatMessage({ id: "ai_briefing_line1" }, {
      emergency: data.emergencyCount,
      caution: data.cautionCount,
      changeText,
    });

    const totalThisWeek = (data.emergencyCount || 0) + (data.cautionCount || 0);
    let line2;
    if (totalThisWeek === 0){
      line2 = formatMessage({ id: "ai_briefing_no_alerts_week" });
    } else {
      const topRoom = (data.topRooms || [])[0];
      const nightSuffix = data.nightConcentrated ? formatMessage({ id: "ai_briefing_night_suffix" }) : "";
      line2 = topRoom
        ? formatMessage({ id: "ai_briefing_top_room_line" }, { room: topRoom.roomName, count: topRoom.count, nightSuffix })
        : "";
    }

    return { line1, line2 };
  };

  const { line1: summaryLine1, line2: summaryLine2 } = buildLines(briefing);
  const topRooms = (briefing && briefing.topRooms) || [];

  return (
    <Card className={clsx(classes.root, className)} {...rest}>
      <CardHeader
        className={classes.title}
        title={formatMessage({ id: "ai_briefing_title" })}
        titleTypographyProps={{ color: "inherit", variant: "h5" }}
      />

      <CardContent>
        {loading && (
          <Typography variant="body1" className={classes.mutedText}>{formatMessage({ id: "ai_briefing_loading" })}</Typography>
        )}

        {!loading && error && (
          <Typography variant="body1" className={classes.mutedText}>{formatMessage({ id: "ai_briefing_error" })}</Typography>
        )}

        {!loading && !error && noData && (
          <Typography variant="body1" className={classes.mutedText}>{formatMessage({ id: "ai_briefing_no_data" })}</Typography>
        )}

        {!loading && !error && !noData && (
          <>
            <Typography variant="h5" className={classes.summaryLine1}>{summaryLine1}</Typography>
            <Typography variant="h6" className={classes.summaryLine2}>{summaryLine2}</Typography>

            {topRooms.length > 0 && (
              <>
                <Typography variant="caption" className={classes.rankCaption}>{formatMessage({ id: "ai_briefing_rank_caption" })}</Typography>
                <Table size="small" className={classes.rankTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>{formatMessage({ id: "ai_briefing_col_room" })}</TableCell>
                    <TableCell align="center">{formatMessage({ id: "ai_briefing_col_type" })}</TableCell>
                    <TableCell align="center">{formatMessage({ id: "ai_briefing_col_last_date" })}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {topRooms.map((room) => (
                    <TableRow key={room.roomName}>
                      <TableCell style={{fontWeight:600}}>{room.roomName}</TableCell>
                      <TableCell align="center" style={{fontWeight:600}}>
                        {[
                          room.emergencyCount > 0 ? formatMessage({ id: "ai_briefing_emergency_count" }, { count: room.emergencyCount }) : null,
                          room.cautionCount > 0 ? formatMessage({ id: "ai_briefing_caution_count" }, { count: room.cautionCount }) : null,
                        ].filter(Boolean).join(" · ")}
                      </TableCell>
                      <TableCell align="center">
                        {room.lastEventTime ? moment(room.lastEventTime).format("MM.DD HH:mm") : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                </Table>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

AiBriefingBoard.propTypes = {
  className: PropTypes.string,
};

export default AiBriefingBoard;

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(1),
  },
  title: {
    color: theme.palette.primary.contrastText,
    height:SIREN_BOARD_TITLE_HEIGHT,
    background: 'linear-gradient(90deg, #4F46E5 0%, #7C3AED 55%, #06B6D4 100%)',
  },
  summaryLine1: {
    lineHeight: 1.5,
    fontWeight: 600,
    color: theme.palette.primary.dark,
  },
  summaryLine2: {
    marginTop: theme.spacing(0.5),
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  mutedText: {
    color: theme.palette.text.secondary,
  },
  rankCaption: {
    display: 'block',
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  rankTable: {
    marginTop: theme.spacing(0.5),
  },
}));
